import { useState } from 'react';

import { WikiSalaryChartResponse } from '~hooks/query/company/useCompanyWikiChart';
import {
  WIKI_CAREER_EMPLOYEE_CATEGORY,
  WIKI_CATEGORY_KEY,
  WIKI_NEW_EMPLOYEE_CATEGORY,
} from '~types/company';

import { CareerType, ModalInfo } from './types';

interface HandleModalOpenParams {
  type: CareerType;
  defaultMenuValue?: WIKI_CATEGORY_KEY;
}

interface EmployeeNavData<T = string> {
  label: string;
  value: T;
}

/**
 * 연봉상세 모달팝업을 함수 호출을 통해 제어할 수 있도록 제공
 *
 * @param data
 * @returns {isModalOpen, setIsModalOpen, modalInfo, handleModalOpen}
 */
export function useModalOpen(data?: WikiSalaryChartResponse) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>();
  const [modalInfo, setModalInfo] = useState<ModalInfo>();
  const { getDefaultMenuValue } = useGetDefaultMenuValue();
  const { getModalInfo } = useGetModalInfo();

  /**
   * - 모달의 type(신입 or 경력)값에 따라 modalInfo를 설정한다.
   * - defaultMenuValue 파라미터가 없다면 getDefaultMenu 함수에서 기본 menu를 가져온다.
   * @param params
   */
  const handleModalOpen = (params: HandleModalOpenParams) => {
    const defaultMenuValue = params.defaultMenuValue || getDefaultMenuValue(params.type, data);
    const newModalInfo = getModalInfo(params.type, defaultMenuValue);

    setModalInfo(newModalInfo);
    setIsModalOpen(true);
  };

  return { isModalOpen, setIsModalOpen, modalInfo, handleModalOpen };
}

export function useConstants() {
  const NEW_EMPLOYEE_NAV_DATA: EmployeeNavData<WIKI_NEW_EMPLOYEE_CATEGORY>[] = [
    { label: '고졸', value: 'HIGHSCHOOL' },
    { label: '초대졸', value: 'COLLEGE' },
    { label: '대졸', value: 'UNIVERSITY' },
    { label: '대학원졸', value: 'MASTER' },
  ];

  const CAREER_EMPLOYEE_NAV_DATA: EmployeeNavData<WIKI_CAREER_EMPLOYEE_CATEGORY>[] = [
    { label: '2-4년', value: 'JIDR' },
    { label: '5-7년', value: 'GJ' },
    { label: '8-10년', value: 'CJ' },
    { label: '10년 초과', value: 'BJ' },
  ];

  const DEFAULT_NEW_EMPLOYEE_VALUE: WIKI_NEW_EMPLOYEE_CATEGORY = NEW_EMPLOYEE_NAV_DATA[2].value;
  const DEFAULT_CAREER_EMPLOYEE_VALUE: WIKI_CAREER_EMPLOYEE_CATEGORY =
    CAREER_EMPLOYEE_NAV_DATA[0].value;

  return {
    NEW_EMPLOYEE_NAV_DATA,
    CAREER_EMPLOYEE_NAV_DATA,
    DEFAULT_NEW_EMPLOYEE_VALUE,
    DEFAULT_CAREER_EMPLOYEE_VALUE,
  };
}

export function useGetDefaultMenuValue() {
  const {
    CAREER_EMPLOYEE_NAV_DATA,
    NEW_EMPLOYEE_NAV_DATA,
    DEFAULT_NEW_EMPLOYEE_VALUE,
    DEFAULT_CAREER_EMPLOYEE_VALUE,
  } = useConstants();

  /**
   * 아래 조건으로 연봉상세 모달팝업의 기본메뉴를 반환한다.
   *
   * [신입]
   *
   * - (1순위) 대졸
   * - (2순위 : 예상연봉="정보없음"일 경우)
   * - 좌측 순으로 정보가 있을 경우노출
   * - ex. 고졸 > 초대졸 > 대졸 > 대학원졸
   *
   * [경력]
   *
   * - (1순위) 2-4년
   * - (2순위 : 예상연봉="정보없음"일 경우)
   * - 좌측 순으로 정보가 있을 경우노출
   * - ex. 2-4년 > 5-7년 > 8-10년 > 10년 초과
   *
   * @param type '신입' | '연봉'
   * @param data WikiSalaryChartResponse
   * @returns menuValue (ex. 'HIGHSCHOOL' or 'COLLEGE' or 'JIDR' or 'GJ')
   */
  const getDefaultMenuValue = (type: CareerType, data?: WikiSalaryChartResponse) => {
    if (!data) {
      return type === '경력' ? DEFAULT_CAREER_EMPLOYEE_VALUE : DEFAULT_NEW_EMPLOYEE_VALUE;
    }

    if (type === '경력') {
      if (data[DEFAULT_CAREER_EMPLOYEE_VALUE].avg) {
        return DEFAULT_CAREER_EMPLOYEE_VALUE;
      }

      const availableMenu = CAREER_EMPLOYEE_NAV_DATA.filter((item) => !!data[item.value].avg);
      if (availableMenu.length > 0) {
        return availableMenu[0].value;
      }

      return DEFAULT_CAREER_EMPLOYEE_VALUE;
    } else {
      if (data[DEFAULT_NEW_EMPLOYEE_VALUE].avg) {
        return DEFAULT_NEW_EMPLOYEE_VALUE;
      }

      const availableMenu = NEW_EMPLOYEE_NAV_DATA.filter((item) => !!data[item.value].avg);
      if (availableMenu.length > 0) {
        return availableMenu[0].value;
      }

      return DEFAULT_NEW_EMPLOYEE_VALUE;
    }
  };

  return { getDefaultMenuValue };
}

export function useGetModalInfo() {
  const { NEW_EMPLOYEE_NAV_DATA, CAREER_EMPLOYEE_NAV_DATA } = useConstants();

  /**
   * modalInfo를 반환한다.
   * @param type
   * @param defaultMenuValue
   * @returns modalInfo
   */
  const getModalInfo = (type: CareerType, defaultMenuValue: WIKI_CATEGORY_KEY) => {
    const obj: ModalInfo = {
      navData: NEW_EMPLOYEE_NAV_DATA,
      title: '신입 예상 연봉',
      defaultMenuValue,
      type,
    };

    if (type === '경력') {
      obj.navData = CAREER_EMPLOYEE_NAV_DATA;
      obj.title = '경력 예상 연봉';
    }

    return obj;
  };

  return { getModalInfo };
}
